import React from 'react';
import type { LibrariesState } from './LibkeyLibrarySection';
import type { SaveStatus } from './LibraryPicker';
import { Link } from 'components/core';
import { Text } from 'components/core/Typography';
import { Inline } from 'components/layout';

const HELPER_IDLE = '';
const HELPER_SAVING = 'Saving…';
const HELPER_SAVED = 'Your changes have been saved';
const HELPER_LOADING_LIBRARIES = 'Loading libraries...';
const HELPER_LIBRARIES_ERROR = "Couldn't load libraries.";

type StatusDescriptor = {
  message: string;
  tone: 'weak' | 'danger';
  // Library-load failures are recoverable, so we offer a retry affordance.
  canRetry: boolean;
};

const statusFor = (
  saveStatus: SaveStatus,
  librariesState: LibrariesState
): StatusDescriptor => {
  // A failed or empty library list takes precedence over the save status: the
  // picker is unusable until the list loads, so any save feedback is moot.
  // `empty` is surfaced as an error state by design.
  if (librariesState.status === 'error' || librariesState.status === 'empty') {
    return { message: HELPER_LIBRARIES_ERROR, tone: 'weak', canRetry: true };
  }

  switch (saveStatus.kind) {
    case 'saving':
      return { message: HELPER_SAVING, tone: 'weak', canRetry: false };
    case 'saved':
      return { message: HELPER_SAVED, tone: 'weak', canRetry: false };
    case 'error':
      return { message: saveStatus.message, tone: 'danger', canRetry: false };
    case 'idle':
      // While idle, surface the library-list load so the picker doesn't look
      // inert on page load. Clears to blank once the list resolves.
      return {
        message:
          librariesState.status === 'loading'
            ? HELPER_LOADING_LIBRARIES
            : HELPER_IDLE,
        tone: 'weak',
        canRetry: false,
      };
  }
};

export type LibraryPickerStatusProps = {
  helperId: string;
  saveStatus: SaveStatus;
  librariesState: LibrariesState;
  onRetryLibraries: () => void;
};

const LibraryPickerStatus = ({
  helperId,
  saveStatus,
  librariesState,
  onRetryLibraries,
}: LibraryPickerStatusProps) => {
  const { message, tone, canRetry } = statusFor(saveStatus, librariesState);

  // A single, always-mounted live region: the picker's aria-describedby always
  // resolves to this node, and swapping only its contents (rather than the node
  // itself) lets screen readers announce changes reliably across states.
  return (
    <Inline
      id={helperId}
      role="status"
      aria-live="polite"
      spacing={3}
      alignX="left"
    >
      <Text variant={tone} size="xs">
        {/* Non-breaking space when blank so the line always reserves its
          height — keeps the container from flickering as the message appears
          and clears. */}
        {message || ' '}
      </Text>
      {canRetry && (
        <Text size="xs">
          <Link type="neutral" onClick={onRetryLibraries}>
            Try again
          </Link>
        </Text>
      )}
    </Inline>
  );
};

export { LibraryPickerStatus };
